import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  AddUsersToOrganizationBody,
  ApplyAIEnhanceBody,
  CreateOrganizationBody,
  GenerateAndAssociateJobChallengeQuestionsBody,
  GenerateQuestionsAndCreateJobCompetitionBody,
  InviteOrgMembersBody,
  MemberAcceptanceBody,
  UpdateJobBody,
  UpdateOrganizationBody,
  UpdateOrgUserBody,
  RoleChangeBodyUserRoleEnum,
} from 'src/openapi';
import { enqueueSnackbar } from 'notistack';
import { EmployerPlanType } from 'src/constants';
import { getFormattedErr } from 'src/helpers';
import ApiClient from 'src/helpers/apiClient';
import type { ProductType } from '../types/employerLayout';

export const getOrgList = createAsyncThunk(
  'get/org',
  async (
    {
      searchQuery = '',
      page_no = 1,
      page_size = 10,
      shouldSelectFirstOrg = true,
    }: {
      searchQuery?: string;
      page_no?: number;
      page_size?: number;
      shouldSelectFirstOrg?: boolean;
    },
    { rejectWithValue, fulfillWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrganizationList({
        name: searchQuery,
        page: page_no.toString(),
        pageSize: page_size.toString(),
      });
      return fulfillWithValue({ shouldSelectFirstOrg, data: response.data });
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchNextOrg = createAsyncThunk(
  'get/next-org',
  async (
    {
      searchQuery = '',
      page_no = '1',
      page_size = '10',
    }: { searchQuery?: string; page_no?: string; page_size?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrganizationList({
        name: searchQuery,
        page: page_no,
        pageSize: page_size,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const exitOrg = createAsyncThunk(
  'put/exit-org',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiUpdateMemberResignation({ orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchOrgUsersList = createAsyncThunk(
  'fetch/org-users-list',
  async (
    { orgId, companyId }: { orgId: string; companyId?: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrganizationUsers(
        { orgId, companyId },
        {
          signal,
        },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const transferOwnership = createAsyncThunk(
  'org/transfer-ownership',
  async (
    { orgId, newOwnerUserId }: { newOwnerUserId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiTransferOwnership({ newOwnerUserId, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const changeOrgUserRole = createAsyncThunk(
  'org/change-role',
  async (
    { userId, role, orgId }: { orgId: string; userId: string; role: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiChangeOrgUserRole({
        orgId,
        roleChangeBody: {
          user_role: role as RoleChangeBodyUserRoleEnum,
          member_user_id: userId,
        },
      });
      enqueueSnackbar('Updated user role successfully', {
        variant: 'success',
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const removeOrgMember = createAsyncThunk(
  'org/remove-member',
  async (
    { email, orgId }: { email: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiRemoveOrganizationMembers({ email, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const postOrgInvite = createAsyncThunk(
  'post/org-invite',
  async (
    { orgId, payload }: { orgId: string; payload: InviteOrgMembersBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiInviteOrganizationMembers({
        orgId,
        inviteOrgMembersBody: payload,
      });
      enqueueSnackbar('Invitation sent successfully', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const onTourDone = createAsyncThunk(
  '',
  async ({ name }: { name: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiUpdateViewedTour({
        onboardingDoneBody: { name },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchOrgDetails = createAsyncThunk(
  'get/org/{org_id}',
  async ({ orgId }: { orgId: string }, { rejectWithValue, signal }) => {
    try {
      const res = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrganizationById({ orgId }, { signal });
      return res.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchJobByIdForEmployer = createAsyncThunk(
  'get-employer-job/org/{org_id}/{job_id}',
  async ({ jobId }: { jobId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobByIdForEmployer({ jobId });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchPostedJobList = createAsyncThunk(
  'org/{org_id}/jobs',
  async (
    {
      query,
      pageNo,
      pageSize,
      orgId,
      isGptJob,
      jobStatus,
      isCompetitive,
      planTypeList,
      // isArchieved,
      planType,
    }: {
      // isArchieved?: boolean;
      query?: string;
      pageNo: number;
      pageSize: number;
      orgId: string;
      isGptJob?: boolean;
      jobStatus?: string;
      isCompetitive?: boolean;
      planTypeList?: ProductType;
      planType?: string[];
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobPostedList(
        {
          isCompetitive,
          isGptJob,
          jobStatus,
          orgId,
          pageNo,
          pageSize,
          planTypeList: planType,
          productType: planTypeList,
          query,
        },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateJob = createAsyncThunk(
  'org/{org_id}/{job_id}/update',
  async (
    {
      jobId,
      payload,
      orgId,
    }: { jobId: string; payload: UpdateJobBody; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpdateJob({ jobId, orgId, updateJobBody: payload });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteJob = createAsyncThunk(
  'org/{org_id}/{job_id}/delete',
  async (
    {
      orgId,
      jobId,
    }: {
      orgId: string;
      jobId: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiDeleteJob({ jobId, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updatePreference = createAsyncThunk(
  'job/candidate-meta-details',
  async ({ obj }: { obj: any }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiUpsertCandidateMetaDetails(obj);
      return response.data;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

export const createOrg = createAsyncThunk(
  'org/create-org',
  async (data: CreateOrganizationBody, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiCreateOrganization({ createOrganizationBody: data });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateOrg = createAsyncThunk(
  'org/updateOrg',
  async (
    // verification_doc_type isn't in the generated body type yet (client not
    // regenerated); it serializes straight through and the backend accepts it.
    {
      id,
      data,
    }: {
      id: string;
      data: UpdateOrganizationBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiUpdateOrganization({
        orgId: id,
        updateOrganizationBody: data,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const uploadVerificationDocuments = createAsyncThunk(
  'files/verification',
  async (
    { orgId, file }: { orgId: string; file: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getFilesApi()
      ).filesApiUploadOrgDocument({ orgId, orgDocument: file });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const sendOTP = createAsyncThunk(
  'sms/otp',
  async ({ obj }: { obj: {} }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getSmsApi()
      ).smsApiGenerateSms({ sendOtpBody: obj });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const validateOTP = createAsyncThunk(
  'sms/validate_otp',
  async ({ obj }: { obj: { otp: number } }) => {
    try {
      const response = await (
        await ApiClient.getSmsApi()
      ).smsApiValidateOtp({ validateOtpBody: obj });
      return response;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

export const uploadCompanyLogo = createAsyncThunk(
  'upload/company_logo',
  async (
    { org_id, image_file }: { org_id?: any; image_file: any },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiUploadOrganizationLogo({
        imageFile: image_file,
        orgId: org_id,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

let abortController: AbortController | null = null;
export const sendCompanyAutocompleteQuery = createAsyncThunk(
  'autocomplete/company_name',
  async ({ query }: { query: string }) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiCompanyAutocomplete(
        { query },
        { signal: abortController.signal },
      );
      return response.data;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

export const checkTeamNameAvailability = createAsyncThunk(
  'checkTeamNameAvailability',
  async (
    { name, companyId }: { name: string; companyId: string },
    { rejectWithValue, signal },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiIsTeamNameAvailable(
        { companyId, teamName: name },
        { signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const updateUserEmail = createAsyncThunk(
  'update/user-email',
  async ({
    email,
    email_verification_otp,
  }: {
    email: string;
    email_verification_otp: string;
  }) => {
    try {
      const response = await (
        await ApiClient.getUserApi()
      ).userApiUpdateUserEmail({
        updateUserEmailBody: { email, email_verification_otp },
      });
      return response.data;
    } catch (err: any) {
      throw err.response.data;
    }
  },
);

export const updateOrgUser = createAsyncThunk(
  'update/org-user',
  async (
    {
      orgId,
      userId,
      data,
    }: { orgId: string; userId: string; data: UpdateOrgUserBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiUpdateOrganizationUser({
        orgId,
        userId,
        updateOrgUserBody: data,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchOrgUser = createAsyncThunk(
  'fetch/org-user',
  async (
    { orgId, userId }: { orgId: string; userId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrgUser({ orgId, userId });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const fetchUploadedProof = async (orgId: string) => {
  const res = await (
    await ApiClient.getOrgApi()
  ).organizationApiGetVerificationDoc({ orgId }, { responseType: 'blob' });

  return res;
};

export const getOrgJobStatistics = createAsyncThunk(
  'get/org-job-summary',
  async ({ orgId }: { orgId: string }, { rejectWithValue, signal }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobStatistics({ orgId }, { signal });
      return response.data;
    } catch (err: any) {
      if (err?.code !== 'ERR_CANCELED') {
        enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      }
      return rejectWithValue(err.response.data);
    }
  },
);

export const getEmployerFreeTrailPlans = createAsyncThunk(
  'get/free-trail-plans',
  async (
    {
      pageNo = 1,
      pageSize = 10,
      planType = EmployerPlanType.TrialWholeOrganization,
      query,
      status = 'Active',
    }: {
      pageNo?: number;
      pageSize?: number;
      planType?: EmployerPlanType;
      query?: string;
      status?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetTrialPlanList({
        page: pageNo,
        pageSize,
        planType,
        query,
        status,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobById = createAsyncThunk(
  'get/job-by-id',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobById({ jobId, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const checkJobDuplicateStatus = createAsyncThunk(
  'job/check-duplicate-status',
  async (
    { jobId, orgId }: { jobId: string; orgId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiCheckDuplicateJob({ jobId, orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

// used to fetch org list for select component.
// DON NOT add this to slice
export const fetchOrgListForSelect = createAsyncThunk(
  'get/org-list-for-select',
  async (
    {
      searchQuery = '',
      page_no = 1,
      page_size = 10,
    }: {
      searchQuery?: string;
      page_no?: number;
      page_size?: number;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetOrganizationList({
        name: searchQuery,
        page: page_no.toString(),
        pageSize: page_size.toString(),
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCompanyAccessibleUsers = createAsyncThunk(
  'get/company-accessible-users',
  async ({ companyId }: { companyId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetCompanyAccessibleUsers({ companyId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const addUserToOrg = createAsyncThunk(
  'post/org/add-user',
  async (
    { orgId, payload }: { orgId: string; payload: AddUsersToOrganizationBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiAddUserToOrganization({
        orgId,
        addUsersToOrganizationBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getPendingOrgInvite = createAsyncThunk(
  'get/pending-org-invite',
  async (_, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiGetPendingOrganizationList();
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const orgInviteAcceptance = createAsyncThunk(
  'put/org-invite-acceptance',
  async (
    { orgId, payload }: { orgId: string; payload: MemberAcceptanceBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiUpdateAcceptanceStatus({
        orgId,
        memberAcceptanceBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getSupportUnreadMessageCount = createAsyncThunk(
  'get/unread-support-message-count',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getChatApi()
      ).chatApiGetChatCount({ orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const deleteOrg = createAsyncThunk(
  'delete/org',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiHardDeleteOrganization({ orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getOrgTransactions = createAsyncThunk(
  'get/org-transaction',
  async ({ orgId }: { orgId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getPaymentApi()
      ).paymentApiGetOrgTransactionsData({ orgId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getStoreItemsByID = createAsyncThunk(
  'store/getStoreItemsbyID',
  async ({ planId }: { planId?: string }, { rejectWithValue }) => {
    try {
      const res = await (
        await ApiClient.getPaymentApi()
      ).paymentApiGetAllItemsPublic({ availableTo: 'Employers', planId });
      return res.data.items || [];
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getCollegesAutocomplete = createAsyncThunk(
  'get/colleges-autocomplete',
  async (
    { jobId, searchQuery }: { jobId: string; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAutocompleteCollegeApplicants({ jobId, query: searchQuery });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);
export const getJobRoleAutocomplete = createAsyncThunk(
  'get/job-role-autocomplete',
  async (
    { jobId, searchQuery }: { jobId: string; searchQuery?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetAutocompleteJobRoleApplicants({ jobId, query: searchQuery });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

let getOrgAutocompleteController: AbortController | null = null;
export const getOrgAutocomplete = createAsyncThunk(
  'org/autocomplete',
  async ({ query }: { query: string }, { rejectWithValue }) => {
    try {
      if (getOrgAutocompleteController) {
        getOrgAutocompleteController.abort();
      }
      getOrgAutocompleteController = new AbortController();

      const response = await (
        await ApiClient.getOrgApi()
      ).organizationApiOrgAutocomplete(
        { searchQuery: query },
        { signal: getOrgAutocompleteController.signal },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateQuestionsAndCreateJobCompetition = createAsyncThunk(
  'POST/generate-questions-and-create-competition',
  async (
    {
      jobId,
      payload,
    }: { jobId: string; payload: GenerateQuestionsAndCreateJobCompetitionBody },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGenerateQuestionsAndCreateJobCompetition({
        jobId,
        generateQuestionsAndCreateJobCompetitionBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const getJobCompetitionStatistics = createAsyncThunk(
  'GET/job-competition-statistics',
  async (
    { jobId, competitionId }: { competitionId: string; jobId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGetJobCompetitionStatistics({ competitionId, jobId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const makeJobChallengeLive = createAsyncThunk(
  'PUT/associate-job-with-competition',
  async ({ competitionId }: { competitionId: string }, { rejectWithValue }) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiPublishJobCompetition({ competitionId });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response.data);
    }
  },
);

export const generateJobChallengeQuestions = createAsyncThunk(
  'competition/add-competition-questions',
  async (
    {
      competitionId,
      jobId,
      payload,
    }: {
      competitionId: string;
      jobId: string;
      payload: GenerateAndAssociateJobChallengeQuestionsBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getJobApi()
      ).jobApiGenerateAndAssociateJobChallengeQuestions({
        competitionId,
        jobId,
        generateAndAssociateJobChallengeQuestionsBody: payload,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
export const regenerateJobChallengeQuestion = createAsyncThunk(
  'competition/regenerate-job-challenge-question',
  async (
    {
      jobId,
      competitionId,
      questionId,
    }: { jobId: string; competitionId: string; questionId: string },
    { rejectWithValue },
  ) => {
    try {
      return await ApiClient.regenerateJobChallengeQuestion(
        jobId,
        competitionId,
        questionId,
      );
    } catch (err: any) {
      const error = err?.response?.data?.error;
      enqueueSnackbar(
        error === 'question_regeneration_failed'
          ? "Couldn't write a better question this time. Please try again."
          : getFormattedErr(error),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);
export const applyAiEnhanceQuestion = createAsyncThunk(
  'question/apply-ai-question-enhance',
  async (
    {
      questionId,
      questionBody,
    }: {
      questionId: string;
      questionBody: ApplyAIEnhanceBody;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getQuestionApi()
      ).questionApiApplyAiEnhanceQuestion({
        questionId,
        applyAIEnhanceBody: questionBody,
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
export const aiEnhanceQuestion = createAsyncThunk(
  'question/ai-question-enhance',
  async (
    {
      questionId,
      userPrompt,
    }: {
      questionId: string;
      userPrompt: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await (
        await ApiClient.getQuestionApi()
      ).questionApiAiEnhanceQuestion({
        questionId,
        aIEnhanceQuestionBody: { user_prompt: userPrompt },
      });
      return response.data;
    } catch (err: any) {
      enqueueSnackbar(getFormattedErr(err?.response?.data?.error));
      return rejectWithValue(err.response?.data);
    }
  },
);
